/**
 * Portal registrations queue (ADR-0016) — pending self-signups awaiting an
 * operator's decision. Lifted out of the Security settings page into its own
 * sidebar entry + grid: the queue is an operational worklist (it changes on its
 * own, it has a count, you act on rows), so it deserves a first-class home, not
 * a card buried under the registration-policy form.
 *
 * Approving runs the backend activation hook (activate + auto-enroll into every
 * `auto_enroll` app); rejecting disables the account. Operator console (Apps
 * plane) — gated by the `apps` RBAC.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, UserCheck, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { appUsersApi } from "@/lib/api-app-users";

export function PortalRegistrationsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const pendingQ = useQuery({
    queryKey: ["app-users", "pending"],
    queryFn: () => appUsersApi.list({ state: "pending", page_size: 100 }),
  });

  const decide = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "approve" | "reject" }) =>
      action === "approve" ? appUsersApi.approve(id) : appUsersApi.reject(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["app-users"] }),
  });

  const pending = pendingQ.data?.items ?? [];

  return (
    <PageContainer>
      <PageHeader
        title={t("portalRegistrations.title", "Registrations")}
        description={t(
          "portalRegistrations.subtitle",
          "Pending self-signups awaiting approval. Approve to activate and auto-enroll, or reject to decline.",
        )}
        actions={pending.length > 0 ? <Badge tone="warning">{pending.length}</Badge> : undefined}
      />
      <PageContent className="space-y-4">
        {pendingQ.isPending ? (
          <div className="flex justify-center py-12 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : pending.length === 0 ? (
          <EmptyState
            icon={UserCheck}
            title={t("portalRegistrations.empty", "No pending registrations")}
            description={t(
              "portalRegistrations.emptyHelp",
              "New self-signups awaiting approval will appear here.",
            )}
          />
        ) : (
          <div className="overflow-hidden rounded-lg border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  <Th>{t("portalRegistrations.email", "Email")}</Th>
                  <Th>{t("portalRegistrations.name", "Name")}</Th>
                  <Th className="w-48">{t("portalRegistrations.requested", "Requested")}</Th>
                  <Th className="w-56 text-right">{t("common.actions", "Actions")}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {pending.map((u) => (
                  <Tr key={u.id}>
                    <Td className="font-medium">{u.email}</Td>
                    <Td className="text-sm text-muted-foreground">{u.full_name ?? "—"}</Td>
                    <Td className="text-xs text-muted-foreground">
                      {new Date(u.created_at).toLocaleString()}
                    </Td>
                    <Td className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={decide.isPending}
                          onClick={() => decide.mutate({ id: u.id, action: "reject" })}
                        >
                          <X className="mr-1 h-4 w-4" />
                          {t("portalRegistrations.reject", "Reject")}
                        </Button>
                        <Button
                          size="sm"
                          disabled={decide.isPending}
                          onClick={() => decide.mutate({ id: u.id, action: "approve" })}
                        >
                          <Check className="mr-1 h-4 w-4" />
                          {t("portalRegistrations.approve", "Approve")}
                        </Button>
                      </div>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>
        )}
      </PageContent>
    </PageContainer>
  );
}
